export type TipoContenido = 'DIGITAL' | 'ARCHIVO' | 'ENLACE_EXTERNO';
export type ModoApertura = 'AUTO' | 'LECTURA_INLINE' | 'DESCARGA';

export interface ArchivoAdjunto {
  id: string;
  documentoId: string;
  nombreArchivo: string;
  mimeType: string;
  tamanoBytes: number | null;
  storagePath: string | null;
  enlaceExterno: string | null;
  modoApertura: ModoApertura;
  descripcion: string | null;
  creadoPor: string;
  creadoEn: string;
  actualizadoEn: string;
}

export type TipologiaProceso = 'PE' | 'PO' | 'PS';

export type TipoDocumental =
  | 'PR'
  | 'NC'
  | 'IT'
  | 'MN'
  | 'PA'
  | 'RE'
  | 'FC'
  | 'FL'
  | 'HE'
  | 'MO'
  | 'PT'
  | 'FR'
  | 'FP'
  | 'FG';

// Matches backend EstadoDocumento enum exactly
export type EstadoDocumento = 'BORRADOR' | 'EN_REVISION' | 'APROBADO' | 'PUBLICADO' | 'ARCHIVADO';

// Legacy: EstadoVersion migrado a EstadoDocumento en BD (prompt 143)
// Se mantiene por compatibilidad con UI de versiones que aún muestra EN_REVISION como "Enviado"
export type EstadoVersion = 'BORRADOR' | 'ENVIADO' | 'APROBADO' | 'RECHAZADO';

export interface Proceso {
  id: string;
  tenantId: string;
  codigo: string;
  tipologia: TipologiaProceso;
  numero?: number;
  nombre: string;
  descripcion: string | null;
  grupoNumero: number;
  grupoNombre: string;
  grupoFuncional: string | null; // computed by backend
  fase?: string | null;
  activo: boolean;
  fechaCreacion?: string | null;
  fechaActualizacion?: string | null;
  fechaVigencia?: string | null;
  fechaUltimaActualizacion?: string | null;
  _count?: { documentos: number };
  documentos?: DocumentoResumen[];
  rolesGobernanza?: RolGobernanzaResumen[];
  representante?: RolProcesoResumen | null;
  direccionResponsable?: RolProcesoResumen | null;
}

export interface RolGobernanzaResumen {
  id?: string;
  tipoRol: string;
  puesto: { id: string; titulo: string; codigo?: string | null };
  persona?: { id?: string; nombre: string; apellidos: string } | null;
}

export interface RolProcesoResumen {
  puesto: { id: string; titulo: string; codigo?: string | null; nivel?: string | null };
  persona?: { id?: string; nombre: string; apellidos: string } | null;
}

export interface DocumentoResumen {
  id: string;
  codigo: string;
  titulo: string;
  tipoDocumental: TipoDocumental;
  nivelDocumental?: string;
  estado: EstadoDocumento;
  direccion?: string;
  creadoEn?: string;
  actualizadoEn?: string;
  tipoContenido?: TipoContenido;
  proceso?: {
    codigo: string;
    nombre: string;
    rolesGobernanza?: Array<{
      tipoRol: string;
      puesto?: {
        titulo?: string | null;
        asignaciones?: Array<{
          persona?: { nombre?: string | null; apellidos?: string | null } | null;
        }>;
      } | null;
    }>;
  };
  versionVigente?: {
    numeroEdicion: number;
    estado: EstadoVersion;
    fechaAprobacion: string | null;
  } | null;
  // Compatibility aliases (mapped from backend response in api.ts)
  nombre: string; // alias for titulo
  edicionActual: number; // derived from versionVigente?.numeroEdicion ?? 1
  empresa: string; // hardcoded 'VALOR·IA Governance'
}

export interface Documento extends DocumentoResumen {
  tenantId: string;
  procesoId: string;
  direccion: string;
  creadoPor: string;
  versiones: VersionResumen[];
  referenciasSalida: ReferenciaCruzada[];
  referenciasEntrada: ReferenciaCruzada[];
}

export interface VersionResumen {
  id: string;
  numeroEdicion: number;
  estado: EstadoVersion;
  creadoPor?: string;
  creadoEn: string;
  fechaAprobacion: string | null;
  fechaPublicacion?: string | null;
  // 0665: asunto+alcance canónicos cross-artefacto. descripcionCambios queda hasta cleanup.
  asunto: string | null;
  alcance: string | null;
  descripcionCambios: string | null;
}

export interface DocumentVersion {
  id: string;
  documentoId: string;
  numeroEdicion: number;
  estado: EstadoVersion;
  contenidoJson: any;
  contenidoHtml?: string | null;
  contenidoPlano?: string | null;
  metadatosJson?: any;
  // 0665: asunto+alcance canónicos cross-artefacto.
  asunto: string | null;
  alcance: string | null;
  descripcionCambios: string | null;
  creadoPor: string;
  aprobadoPor: string | null;
  fechaAprobacion: string | null;
  fechaPublicacion?: string | null;
  creadoEn: string;
  actualizadoEn: string;
  autor?: { nombreCompleto: string; email: string };
  aprobador?: { nombreCompleto: string; email: string } | null;
  firmado?: boolean;
  fechaFirma?: string | null;
}

export interface ReferenciaCruzada {
  id: string;
  documentoOrigenId: string;
  documentoDestinoId: string | null;
  destinoExternoUrl?: string | null;
  codigoDestino: string;
  valida: boolean;
  documentoOrigen?: { codigo: string; titulo: string };
  documentoDestino?: { codigo: string; titulo: string } | null;
}

export interface PaginacionResponse<T> {
  datos: T[];
  paginacion: {
    pagina: number;
    porPagina: number;
    total: number;
    totalPaginas: number;
  };
}

export const TIPO_DOCUMENTAL_LABEL: Record<TipoDocumental, string> = {
  PR: 'Procedimiento',
  NC: 'Norma Corporativa',
  IT: 'Instruccion de Trabajo',
  MN: 'Manual',
  PA: 'Plan de Accion',
  RE: 'Reporte',
  FC: 'Ficha de Caracterizacion',
  FL: 'Flujograma',
  HE: 'Herramienta',
  MO: 'Modelo',
  PT: 'Protocolo',
  FR: 'Ficha de Rol Funcional',
  FP: 'Ficha de Puesto',
  FG: 'Ficha de Rol de Gobernanza',
};

export const ESTADO_CONFIG: Record<EstadoDocumento, { label: string; color: string; bg: string }> =
  {
    BORRADOR: { label: 'Borrador', color: 'text-gray-700', bg: 'bg-gris' },
    EN_REVISION: { label: 'En Revision', color: 'text-amber-800', bg: 'bg-naranja' },
    APROBADO: { label: 'Aprobado', color: 'text-white', bg: 'bg-azul2' },
    PUBLICADO: { label: 'Vigente', color: 'text-white', bg: 'bg-verde' },
    ARCHIVADO: { label: 'Archivado', color: 'text-white', bg: 'bg-rojo' },
  };

export const TIPOLOGIA_LABEL: Record<TipologiaProceso, string> = {
  PE: 'Procesos Estrategicos',
  PO: 'Procesos Operativos',
  PS: 'Procesos de Soporte',
};

// ─── Sprint 2: Secciones, Bloques, Sugerencias, Asignaciones, Glosario ─────

export type TipoBloque =
  | 'PARRAFO'
  | 'TABLA'
  | 'LISTA'
  | 'IMAGEN'
  | 'CALLOUT'
  | 'REFERENCIA_CRUZADA'
  | 'ENCABEZADO_SECCION';

export type OrigenContenido = 'MANUAL' | 'IA_GENERADO' | 'IA_MODIFICADO' | 'IMPORTADO';

export type TipoSugerencia = 'REEMPLAZO' | 'INSERCION' | 'ELIMINACION' | 'FORMATO' | 'ESTRUCTURA';

export type EstadoSugerencia = 'PENDIENTE' | 'ACEPTADA' | 'RECHAZADA' | 'EXPIRADA';

export type CategoriaTermino =
  | 'INGENIERIA'
  | 'FASE'
  | 'TIPO_DOCUMENTAL'
  | 'ROL'
  | 'GENERAL'
  | 'TECNICO';

export type ModoVIA =
  | 'MODO_1_GENERACION'
  | 'MODO_2_CORRECCION'
  | 'MODO_3_AUDITORIA'
  | 'MODO_4_TRADUCCION'
  | 'MODO_5_CHAT';

export interface SeccionDocumento {
  id: string;
  tenantId: string;
  versionDocumentoId: string;
  titulo: string;
  nivel: number;
  orden: number;
  seccionPadreId: string | null;
  obligatoria: boolean;
  creadoEn: string;
  actualizadoEn: string;
  bloques: BloqueContenido[];
  hijas?: SeccionDocumento[];
}

export interface BloqueContenido {
  id: string;
  tenantId: string;
  seccionDocumentoId: string;
  tipo: TipoBloque;
  contenido: Record<string, unknown>;
  orden: number;
  origenContenido: OrigenContenido;
  metadata: Record<string, unknown> | null;
  modificadoPor: string | null;
  modificadoEn: string | null;
  creadoEn: string;
}

export interface SugerenciaContenido {
  id: string;
  tenantId: string;
  bloqueContenidoId: string;
  seccionDocumentoId: string | null;
  tipo: TipoSugerencia;
  contenidoOriginal: Record<string, unknown> | null;
  contenidoPropuesto: Record<string, unknown>;
  motivo: string | null;
  estado: EstadoSugerencia;
  generadoPor: string;
  modoVIA: string | null;
  resueltoPor: string | null;
  resueltoEn: string | null;
  creadoEn: string;
  bloqueContenido?: { id: string; tipo: TipoBloque; seccionDocumentoId: string };
}

export interface AsignacionProceso {
  id: string;
  tenantId: string;
  procesoId: string;
  usuarioId: string;
  rol: string;
  creadoEn: string;
  proceso?: { id: string; codigo: string; nombre: string; tipologia?: TipologiaProceso };
  usuario?: { id: string; nombreCompleto: string; email: string; rol?: string };
}

export interface GlosarioTermino {
  id: string;
  tenantId: string;
  termino: string;
  definicion: string;
  categoria: CategoriaTermino;
  sinonimos: string[];
  noTraducir: boolean;
  idioma: string;
  creadoEn: string;
  actualizadoEn: string;
}
